import {
  getItem,
  type InventoryState,
  isUpgradeable,
  type UpgradeRejected,
  type UpgradeResult,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { benchHead, purseChip, setPurse } from './bench.js';
import { fillPieceList, type Piece, piecesOf } from './craft-pieces.js';
import { closeButton } from './hud.js';
import { hideItemHover } from './item-hover.js';
import { kitTitle } from './kit.js';
import { playUpgradeFail, playUpgradeSuccess } from './sfx.js';
import { renderSmithDetail } from './smith-detail.js';
import { uiFormat, uiText } from './text.js';
import './smith.css';

/**
 * The smith's window (ADR 0013): pick a piece, worn or in the bag, see what
 * the next level gives, how likely it is, and what it costs; then ask. Like
 * every panel it only sends the intent and redraws from what the server says:
 * the preview uses the same rules as the server, the outcome is the server's.
 */

export interface SmithPanelOptions {
  icons: CssAtlas | null;
  onUpgrade: (itemId: string) => void;
}

const REFUSALS: Record<UpgradeRejected['reason'], string> = {
  invalid: 'Demande invalide.',
  'unknown-item': 'Tu ne possèdes plus cette pièce.',
  'not-upgradeable': 'Le forgeron ne peut rien faire de cet objet.',
  'max-level': 'Cette pièce est déjà à +10.',
  'missing-materials': 'Il te manque des matériaux.',
  'missing-gold': 'Il te manque de l’or.',
  'too-far': 'Approche-toi du forgeron.',
  'rate-limited': 'Le forgeron souffle un instant.',
  camp: 'Au camp, Hald ne renforce que l’épée rare fondue chez Garin.',
};

export class SmithPanel {
  private readonly root: HTMLElement;
  private readonly purse: HTMLElement;
  private readonly list: HTMLElement;
  private readonly detail: HTMLElement;
  private readonly message: HTMLElement;
  private inventory: InventoryState | undefined;
  private selectedId: string | undefined;
  private inReach = false;
  /** Between the click and the server's answer: no second attempt meanwhile. */
  private pending = false;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.close();
  };

  constructor(private readonly options: SmithPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'smith smith--forge';
    this.root.dataset.panel = 'forge';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Forgeron'));

    const title = document.createElement('h2');
    title.className = 'smith__title';
    kitTitle(title, 'stat-upgrade');
    title.textContent = uiText('Forgeron');
    const close = closeButton('Fermer (Échap)', () => {
      this.close();
    });
    title.append(close);

    this.purse = purseChip();
    const head = benchHead(uiText('Choisis une pièce, portée ou dans ton sac.'), this.purse);
    this.list = document.createElement('div');
    this.list.className = 'smith__list';
    this.detail = document.createElement('div');
    this.detail.className = 'smith__detail';
    this.message = document.createElement('p');
    this.message.className = 'smith__message';
    this.message.setAttribute('aria-live', 'polite');

    const body = document.createElement('div');
    body.className = 'smith__body';
    body.append(this.list, this.detail);
    this.root.append(title, head, body, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(): void {
    this.root.hidden = false;
    this.message.textContent = '';
    this.render();
  }

  close(): void {
    hideItemHover();
    this.root.hidden = true;
  }

  setInventory(state: InventoryState): void {
    this.inventory = state;
    this.render();
  }

  /** Whether the player stands by the smith; the button waits until they do. */
  setInReach(inReach: boolean): void {
    if (inReach === this.inReach) return;
    this.inReach = inReach;
    this.render();
  }

  showResult(result: UpgradeResult): void {
    this.pending = false;
    const name = uiText(getItem(result.templateId)?.name ?? result.templateId);
    const success = result.outcome === 'success';
    this.say(
      success
        ? uiFormat('Réussite ! {name} passe à +{level}.', { name, level: result.level })
        : uiFormat('Échec. {name} reste à +{level}, les matériaux sont perdus.', {
            name,
            level: result.level,
          }),
      success ? 'success' : 'fail',
    );
    if (success) playUpgradeSuccess();
    else playUpgradeFail();
    this.render();
  }

  showRejected(rejection: UpgradeRejected): void {
    this.pending = false;
    this.say(REFUSALS[rejection.reason], 'refused');
    this.render();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }

  /** A line under the window and a short flash of the whole frame. */
  private say(text: string, tone: 'success' | 'fail' | 'refused'): void {
    this.message.textContent = uiText(text);
    this.message.dataset.tone = tone;
    this.root.classList.remove('smith--success', 'smith--fail');
    if (tone === 'refused') return;
    // Reflow between removing and adding, so the animation replays every time.
    this.root.getBoundingClientRect();
    this.root.classList.add(tone === 'success' ? 'smith--success' : 'smith--fail');
  }

  /** Worn pieces first, then the bag in slot order: only what the smith can work on. */
  private pieces(): Piece[] {
    return piecesOf(this.inventory, (_, template) => isUpgradeable(template));
  }

  /** How many of a template lie in the bag, all stacks together. */
  private owned(templateId: string): number {
    return (this.inventory?.bag ?? [])
      .filter((entry) => entry.item.templateId === templateId)
      .reduce((sum, entry) => sum + entry.item.quantity, 0);
  }

  private render(): void {
    if (this.root.hidden) return;
    setPurse(this.purse, this.inventory?.gold ?? 0);
    const pieces = this.pieces();
    if (!pieces.some((piece) => piece.instance.id === this.selectedId)) {
      this.selectedId = pieces[0]?.instance.id;
    }

    fillPieceList(this.list, pieces, {
      icons: this.options.icons,
      selectedId: this.selectedId,
      empty: uiText('Rien à améliorer : apporte une pièce d’équipement.'),
      onPick: (piece) => {
        this.selectedId = piece.instance.id;
        this.message.textContent = '';
        this.render();
      },
    });

    hideItemHover();
    this.detail.replaceChildren();
    const piece = pieces.find((candidate) => candidate.instance.id === this.selectedId);
    if (piece === undefined) return;
    renderSmithDetail(this.detail, {
      instance: piece.instance,
      worn: piece.worn,
      icons: this.options.icons,
      purse: this.inventory?.gold ?? 0,
      owned: (templateId) => this.owned(templateId),
      inReach: this.inReach,
      pending: this.pending,
      onUpgrade: (itemId) => {
        this.pending = true;
        this.options.onUpgrade(itemId);
      },
    });
  }
}
